import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { type FormEvent, useEffect, useState } from "react";
import {
  downloadCultivationCloseXlsx,
  getCultivationClose,
  listEntityReportLocations,
  reportLocationLabel,
  type CultivationCloseReport,
  type CultivationCloseSchedule,
  type EntityReportLocation,
} from "@/lib/api";
import { useOrganization } from "@/lib/organization";

const SCHEDULES: Array<{ id: string; label: string }> = [
  { id: "adjustment_summary", label: "Adjustment summary" },
  { id: "monthly_plant_inventory", label: "Plant inventory" },
  { id: "yearly_wip", label: "Yearly work in progress" },
  { id: "raw_material", label: "Raw material" },
  { id: "harvest_calc", label: "Harvest" },
  { id: "destroy_calc", label: "Destroy" },
  { id: "transfer_calc", label: "Transfers" },
  { id: "cogs", label: "Cost of goods sold" },
  { id: "excise", label: "Excise" },
  { id: "pnl", label: "Profit and loss" },
  { id: "journal_register", label: "Journals" },
];

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : "The cultivation close could not be loaded.";
}

function filled(schedule: CultivationCloseSchedule | undefined): schedule is CultivationCloseSchedule {
  return Boolean(schedule && (schedule.rows.length > 0 || schedule.columns.length > 0));
}

export function CultivationClosePage() {
  const { scope } = useOrganization();
  const [locations, setLocations] = useState<EntityReportLocation[]>([]);
  const [licenseId, setLicenseId] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [report, setReport] = useState<CultivationCloseReport | null>(null);
  const [tab, setTab] = useState(SCHEDULES[0]!.id);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!scope?.organization_id || !scope.legal_entity_id) return;
    let cancelled = false;
    listEntityReportLocations(scope.organization_id, scope.legal_entity_id)
      .then((rows) => {
        if (cancelled) return;
        setLocations(rows);
        setLicenseId((current) => current || (rows.length === 1 ? rows[0]!.id : ""));
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(messageOf(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [scope?.organization_id, scope?.legal_entity_id]);

  const load = (event?: FormEvent) => {
    event?.preventDefault();
    if (!licenseId || !periodEnd) {
      setError("Choose a location and the last day of the locked close.");
      return;
    }
    setLoading(true);
    setError(null);
    getCultivationClose({ license_id: licenseId, period_end: periodEnd })
      .then((next) => {
        setReport(next);
        const first = SCHEDULES.find((item) => filled(next.schedules?.[item.id]));
        if (first) setTab(first.id);
      })
      .catch((cause: unknown) => {
        setReport(null);
        setError(messageOf(cause));
      })
      .finally(() => setLoading(false));
  };

  const visible = SCHEDULES.filter((item) => filled(report?.schedules?.[item.id]));
  const active = report?.schedules?.[tab];

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Cultivation close"
        description="Schedules from a locked cultivation close, for one location."
      />
      <form className="flex flex-wrap items-end gap-3" onSubmit={load}>
        <label className="grid gap-1 text-sm">
          Location
          <select
            aria-label="Location"
            className="h-10 rounded-md border border-[var(--border)] bg-[var(--background)] px-3"
            value={licenseId}
            onChange={(event) => setLicenseId(event.target.value)}
          >
            <option value="">Choose a location</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {reportLocationLabel(location, locations)}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          Period end
          <Input
            type="date"
            aria-label="Period end"
            value={periodEnd}
            onChange={(event) => setPeriodEnd(event.target.value)}
          />
        </label>
        <Button type="submit" disabled={loading}>
          {loading ? "Loading..." : "Show schedules"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={!licenseId || !periodEnd || loading}
          onClick={() => {
            setError(null);
            downloadCultivationCloseXlsx({ license_id: licenseId, period_end: periodEnd }).catch((cause: unknown) => {
              setError(messageOf(cause));
            });
          }}
        >
          Download workbook
        </Button>
      </form>
      {error ? (
        <p role="alert" className="text-sm text-[var(--danger)]">{error}</p>
      ) : null}
      {report ? (
        <div className="grid gap-3">
          <p className="text-sm text-[var(--muted-foreground)]">
            {report.period?.label ? `Locked close ${report.period.label}` : "Locked close"}
          </p>
          {visible.length === 0 ? (
            <p className="text-sm">This close has no schedule rows.</p>
          ) : (
            <Tabs
              tabs={visible.map((item) => ({ value: item.id, label: item.label }))}
              value={tab}
              onValueChange={setTab}
              label="Cultivation schedules"
            >
              {active ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <caption className="pb-2 text-left font-medium">{active.title || visible.find((item) => item.id === tab)?.label}</caption>
                    <thead>
                      <tr>
                        {active.columns.map((column) => (
                          <th key={column} className="border-b border-[var(--border)] px-2 py-1 text-left">{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {active.rows.map((row) => (
                        <tr key={`${row.label}-${row.values.join("|")}`}>
                          <td className="px-2 py-1">{row.label}</td>
                          {row.values.map((value, index) => (
                            <td key={`${row.label}-${index}`} className="px-2 py-1">{String(value)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </Tabs>
          )}
        </div>
      ) : null}
    </div>
  );
}
